import { ReactNode, useEffect, useState } from 'react'

import { Box, Collapse, Divider, IconButton, Link, Typography, useTheme } from '@mui/material'
import { InnerHtml, List, ListItem, Loading } from 'components/ui'
import { useMappedTranslation, usePageTitle } from 'hooks'
import IconArrow from 'pages/policies/assets/images/arrow.svg'
import { Accordion, handleDownload } from 'pages/policies/utils'

import { AutomationTagIds } from '../../../assets/test/test-automation-tag-id'

type Props = {
  title: string
  titleIcon?: string
  isLoading: boolean
  detail?: string
  accordions?: Accordion[]
  action?: ReactNode
  minorButton?: ReactNode
}
export default function Detail({ title, titleIcon, isLoading, detail, accordions, action, minorButton }: Props) {
  const t = useMappedTranslation()
  usePageTitle(title)

  const theme = useTheme()
  const [openTab, setOpenTab] = useState<number>(-1)

  useEffect(() => {
    const grecaptchaBadge = document.querySelector('.grecaptcha-badge') as HTMLElement
    if (grecaptchaBadge) {
      grecaptchaBadge.style.visibility = 'hidden'
    }

    return () => {
      const grecaptchaBadge = document.querySelector('.grecaptcha-badge') as HTMLElement
      if (grecaptchaBadge) {
        grecaptchaBadge.style.visibility = ''
      }
    }
  }, [])

  const handleListButton = (index) => {
    if (openTab === index) {
      setOpenTab(-1)
    } else {
      setOpenTab(index)
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          {titleIcon && <Box component='img' src={titleIcon} sx={{ width: '48px', height: '48px', mb: '8px' }} alt='' />}
          <Box>
            <Typography
              sx={{
                fontSize: { xs: '36px', lg: '48px' },
                lineHeight: { xs: '48px', lg: '62px' },
                width: { lg: '1040px' },
                fontWeight: 500,
                color: theme.palette.mode === 'light' ? '#252525' : '#FAFAFA',
                textAlign: 'center'
              }}
              aria-level={1}
              role='heading'
              data-testid='TypographyA8BC4932'
            >
              {title}
            </Typography>
          </Box>
        </Box>
        <Box width='100%'>
          {!isLoading && (
            <>
              <Box sx={{ mt: '52px', textAlign: 'right', width: '100%', height: '24px' }}>
                <Link
                  component='button'
                  type='button'
                  color={theme.palette.mode === 'dark' ? '#A9A9A9' : '#636363'}
                  sx={{
                    cursor: 'pointer',
                    fontSize: { xs: '16px', lg: '20px' },
                    fontHeight: '24px',
                    fontWeight: '700'
                  }}
                  data-testid={AutomationTagIds['button.policies.download']}
                  onClick={() => handleDownload(title || t('policy.privacy.title'), detail, accordions)}
                >
                  {t('common.button.download')}
                </Link>
              </Box>
              {minorButton && <Box sx={{ mt: '36px' }}>{minorButton}</Box>}
            </>
          )}
        </Box>
      </Box>
      <Box sx={{ width: '100%' }}>
        {isLoading ? (
          <Loading fullScreen />
        ) : (
          <Box>
            {detail && (
              <Box sx={{ marginTop: { xs: '24px', lg: '36px' } }}>
                <InnerHtml sx={{ fontSize: '16px', lineHeight: '24px', letterSpacing: '0px', wordBreak: 'break-word' }}>
                  {detail}
                </InnerHtml>
              </Box>
            )}

            {accordions && accordions.length >= 1 && (
              <Box sx={{ marginTop: '24px' }}>
                <Divider />
                <List sx={{ padding: 0, width: '100%' }}>
                  {accordions.map((item, index) => (
                    <Box
                      sx={{
                        width: '100%',
                        '&:focus-within:has(:focus-visible)': {
                          outline: `2px solid ${theme.palette.mode === 'dark' ? '#98CBFD' : '#0072DE'}`
                        }
                      }}
                      onClick={() => handleListButton(index)}
                      data-testid='BoxB65B2EA9'
                    >
                      <ListItem
                        sx={{
                          width: '100%',
                          '& .MuiListItemSecondaryAction-root': { height: '100%' },
                          padding: { xs: '30px 0', lg: '36px 0' },
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                        secondaryAction={
                          <Box sx={{ height: '100%' }}>
                            <IconButton
                              onClick={() => handleListButton(index)}
                              sx={{
                                width: '24px',
                                height: '24px',
                                '&:focus-visible': {
                                  outline: 'none !important',
                                  '& .MuiTouchRipple-root': {
                                    display: 'none'
                                  }
                                }
                              }}
                              aria-label={
                                openTab === index
                                  ? t('iam.Accessibility.Button.Attribute.view.less')
                                  : t('iam.Accessibility.Button.Attribute.view.more')
                              }
                              data-testid='IconButtonD59F31E1'
                            >
                              <Box
                                component='img'
                                alt=''
                                src={IconArrow}
                                sx={{
                                  transform: openTab === index ? 'rotate(-90deg)' : 'rotate(90deg)'
                                }}
                              />
                            </IconButton>
                          </Box>
                        }
                        title={
                          <InnerHtml
                            sx={{
                              width: { xs: '264px', lg: '100%' },
                              marginRight: '24px',
                              ontWeight: 'bold',
                              fontSize: { xs: '19px', lg: '20px' },
                              lineHeight: { xs: '24px', lg: '24px' },
                              color: index === openTab ? theme.palette.primary.main : 'text.primary',
                              '& b': { lineHeight: '24px' },
                              '& img': { verticalAlign: 'bottom !important' }
                            }}
                          >
                            {item.title}
                          </InnerHtml>
                        }
                      ></ListItem>
                      <Collapse in={index === openTab}>
                        <Box sx={{ marginTop: '23px' }}>
                          <InnerHtml sx={{ fontSize: '15px', lineHeight: '22px', fontWeight: '300' }}>
                            {item.description}
                          </InnerHtml>
                        </Box>
                      </Collapse>
                      <Divider />
                    </Box>
                  ))}
                </List>
              </Box>
            )}

            {action && (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                {action}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}
